import { AvatarFragment$key } from '@/__generated__/AvatarFragment.graphql'
import {
  EAddressType,
  EZoraTokenClaimStatus,
  useAccountAllocationsDataQuery,
} from '@/__generated__/useAccountAllocationsDataQuery.graphql'
import { ZORA_CLAIM_CONTRACT_ENV } from '@/constants/keys'
import { DEFAULT_CHAIN_ID } from '@/lib/chains'
import { subscribeToClaimUpdates } from '@/utils/relayUtils'
import { shortenAddress } from '@ourzora/utils'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { Address } from 'viem'

const query = graphql`
  query useAccountAllocationsDataQuery(
    $identifiers: [String!]!
    $chainId: Int!
    $zoraClaimContractEnv: EZoraClaimContractEnv!
  ) {
    zoraTokenAllocation(
      identifierWalletAddresses: $identifiers
      chainId: $chainId
      zoraClaimContractEnv: $zoraClaimContractEnv
    ) {
      totalTokensEarned {
        totalTokens
        walletAllocation {
          claimStatus
          tokens
          walletAddress
          walletType
          walletProfile {
            id
            handle
            ... on GraphQLAccountProfile {
              smartWallet {
                smartWalletConfig {
                  owners {
                    ownerAddress
                    ownerIndex
                  }
                }
              }
              externalWallet {
                walletAddress
              }
            }
            ...AvatarFragment
          }
        }
      }
    }
  }
`

interface WalletAllocation {
  claimStatus: EZoraTokenClaimStatus
  tokens: number
  walletAddress: Address
  walletType?: EAddressType
  handle: string
  uapiAccountExternalWallet?: Address | null
}

export interface AccountAllocation {
  avatarKey?: AvatarFragment$key | null
  handle: string
  totalTokens: number
  externalWallet?: WalletAllocation
  smartWallet?: WalletAllocation & { owners: Address[] }
  embeddedWallet?: WalletAllocation
}

interface PendingClaim {
  address: string
  walletType: EAddressType
  timestamp: number
}

const POLLING_INTERVAL = 2000 // 2 seconds
const MAX_POLLING_DURATION = 20000 // 20 seconds

export function useAccountAllocationsData({
  identifiers,
  onStartPolling,
  onStopPolling,
  setStopPollingRef,
}: {
  identifiers: string[]
  onStartPolling?: () => void
  onStopPolling?: () => void
  setStopPollingRef?: (fn: () => void) => void
}) {
  // Store data request key to trigger rerenders
  const [fetchKey, setFetchKey] = useState(0)

  // Store pending claim information
  const pendingClaimsRef = useRef<PendingClaim[]>([])

  // Single polling interval reference
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Function to refresh data by updating the fetch key
  const refreshData = useCallback(() => {
    setFetchKey((prev) => prev + 1)
  }, [])

  // Helper to determine if we need to continue polling
  const shouldContinuePolling = useCallback(() => {
    return pendingClaimsRef.current.length > 0
  }, [])

  // Stop polling helper
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
      onStopPolling?.()
    }
  }, [onStopPolling])

  // Make the stopPolling function available to parent components
  useEffect(() => {
    if (setStopPollingRef) {
      setStopPollingRef(stopPolling)
    }
  }, [stopPolling, setStopPollingRef])

  // Start polling for claim updates
  const startPolling = useCallback(() => {
    // Clean up any existing polling interval
    stopPolling()

    // Notify parent that polling is starting
    onStartPolling?.()

    // Start new polling interval
    pollingIntervalRef.current = setInterval(() => {
      // Clean out stale pending claims older than MAX_POLLING_DURATION
      const now = Date.now()
      const initialLength = pendingClaimsRef.current.length

      // Remove claims that have been pending too long
      pendingClaimsRef.current = pendingClaimsRef.current.filter((claim) => {
        return now - claim.timestamp < MAX_POLLING_DURATION
      })

      // If we removed any claims, log it
      if (initialLength !== pendingClaimsRef.current.length) {
        console.log('Removed stale pending claims after timeout')
      }

      // Always refresh the data at each polling interval
      refreshData()

      // Stop polling if no longer needed
      if (!shouldContinuePolling()) {
        stopPolling()
      }
    }, POLLING_INTERVAL)

    // Immediate refresh on start
    refreshData()
  }, [refreshData, shouldContinuePolling, stopPolling, onStartPolling])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  // Subscribe to claim updates from other components
  useEffect(() => {
    const handleClaimUpdate = (walletAddress: string, walletType: EAddressType) => {
      if (!walletAddress) return

      const normalizedAddress = walletAddress.toLowerCase()

      // Check if this claim is already pending
      const alreadyPending = pendingClaimsRef.current.some(
        (claim) => claim.address === normalizedAddress && claim.walletType === walletType
      )

      if (alreadyPending) return

      // Add new pending claim
      pendingClaimsRef.current.push({
        address: normalizedAddress,
        walletType,
        timestamp: Date.now(),
      })

      // Start polling if not already polling
      if (!pollingIntervalRef.current) {
        startPolling()
      } else {
        // If already polling, just refresh the data
        refreshData()
      }
    }

    // Set up subscription and return cleanup function
    return subscribeToClaimUpdates(handleClaimUpdate)
  }, [refreshData, startPolling])

  // Fetch data with the current fetch key
  const data = useLazyLoadQuery<useAccountAllocationsDataQuery>(
    query,
    {
      identifiers,
      chainId: DEFAULT_CHAIN_ID,
      zoraClaimContractEnv: ZORA_CLAIM_CONTRACT_ENV,
    },
    {
      fetchPolicy: 'store-and-network',
      fetchKey: String(fetchKey),
    }
  )

  // Process the allocations data
  const allocations = useMemo(() => {
    const result: Record<string, AccountAllocation> = {}

    if (!data?.zoraTokenAllocation?.totalTokensEarned?.walletAllocation) {
      return result
    }

    // Track claims that are now confirmed by the backend
    const confirmedClaims: PendingClaim[] = []

    data.zoraTokenAllocation.totalTokensEarned.walletAllocation.forEach((wallet) => {
      if (!wallet.walletProfile?.id) return

      const id: string = wallet.walletProfile.id
      const walletAddr = wallet.walletAddress.toLowerCase()
      const walletType = wallet.walletType

      // Check if this wallet has a pending claim
      const pendingClaimIndex = pendingClaimsRef.current.findIndex(
        (claim) => claim.address === walletAddr && claim.walletType === walletType
      )

      const isPendingClaim = pendingClaimIndex !== -1

      // Handle confirmed claims
      if (isPendingClaim && wallet.claimStatus === 'CLAIMED') {
        confirmedClaims.push(pendingClaimsRef.current[pendingClaimIndex])
      }

      // Initialize account if it doesn't exist
      if (!result[id]) {
        result[id] = {
          handle: wallet.walletProfile?.handle ?? shortenAddress(wallet.walletAddress),
          totalTokens: 0,
          avatarKey: wallet.walletProfile,
        }
      }

      // Add tokens to account total
      result[id].totalTokens += wallet.tokens

      // Determine effective claim status (pending claims appear as claimed)
      const effectiveClaimStatus = isPendingClaim ? 'CLAIMED' : wallet.claimStatus

      // Add wallet to appropriate allocation type
      switch (wallet.walletType) {
        case 'PRIVY':
          result[id].embeddedWallet = {
            walletAddress: wallet.walletAddress as Address,
            claimStatus: effectiveClaimStatus,
            tokens: wallet.tokens,
            walletType: wallet.walletType,
            uapiAccountExternalWallet:
              wallet.walletProfile.externalWallet?.walletAddress || null,
            handle: shortenAddress(wallet.walletAddress),
          }
          break
        case 'SMART_WALLET':
          result[id].smartWallet = {
            walletAddress: wallet.walletAddress as Address,
            claimStatus: effectiveClaimStatus,
            tokens: wallet.tokens,
            walletType: wallet.walletType,
            handle: shortenAddress(wallet.walletAddress),
            uapiAccountExternalWallet:
              wallet.walletProfile.externalWallet?.walletAddress || null,
            owners:
              wallet.walletProfile.smartWallet?.smartWalletConfig?.owners?.map(
                (owner) => owner.ownerAddress
              ) ?? [],
          }
          break
        case 'EXTERNAL':
        default:
          result[id].externalWallet = {
            walletAddress: wallet.walletAddress as Address,
            claimStatus: effectiveClaimStatus,
            tokens: wallet.tokens,
            walletType: wallet.walletType ?? 'EXTERNAL',
            handle: shortenAddress(wallet.walletAddress),
            uapiAccountExternalWallet:
              wallet.walletProfile.externalWallet?.walletAddress || null,
          }
          break
      }
    })

    // Remove claims that have been confirmed by the backend
    if (confirmedClaims.length > 0) {
      pendingClaimsRef.current = pendingClaimsRef.current.filter(
        (pendingClaim) =>
          !confirmedClaims.some(
            (confirmedClaim) =>
              confirmedClaim.address === pendingClaim.address &&
              confirmedClaim.walletType === pendingClaim.walletType
          )
      )

      // If we've removed all pending claims, stop polling
      if (pendingClaimsRef.current.length === 0 && pollingIntervalRef.current) {
        stopPolling()
      }
    }

    return result
  }, [data, stopPolling])

  return allocations
}
